/**
 * Gráfico de Comparativo Temporal
 * Mostra evolução do fluxo ao longo de dias, semanas e meses
 * com comparação com períodos anteriores USANDO DADOS REAIS
 */

import React, { useState, useMemo } from 'react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import { TrendingUp, TrendingDown, Minus, Calendar, ArrowRight, Info, X } from 'lucide-react';

type ViewMode = 'daily' | 'weekly' | 'monthly';

interface Detection {
  id: string;
  created_at: string;
  num_persons: number;
  camera_id?: string;
}

interface TemporalComparisonChartProps {
  detections?: Detection[];
  periodStart?: string;
  periodEnd?: string;
  prevYearData?: { date: string; people_count: number }[];
  className?: string;
}

export const TemporalComparisonChart: React.FC<TemporalComparisonChartProps> = ({
  detections = [],
  periodStart,
  periodEnd,
  prevYearData,
  className = '',
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('daily');
  const [showInfo, setShowInfo] = useState(false);

  // Processar dados reais baseado no modo de visualização
  const chartData = useMemo(() => {
    if (!detections || detections.length === 0) {
      return [];
    }

    // Mapa do ano anterior para comparativo
    const prevByDate: Record<string, number> = {};
    if (prevYearData) {
      prevYearData.forEach(d => { prevByDate[d.date] = d.people_count; });
    }
    const hasPrevYearData = Object.keys(prevByDate).length > 0;

    // Helper: agrega dados históricos do ano anterior para o bucket agrupado
    const getPrevYearValue = (dateKey: string): number => {
      if (!hasPrevYearData) return 0;
      if (viewMode === 'daily') {
        const [y, m, d] = dateKey.split('-').map(Number);
        return prevByDate[`${y - 1}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`] ?? 0;
      }
      if (viewMode === 'weekly') {
        const [y, m, d] = dateKey.split('-').map(Number);
        const start = new Date(y - 1, m - 1, d);
        let total = 0;
        for (let i = 0; i < 7; i++) {
          const cur = new Date(start);
          cur.setDate(cur.getDate() + i);
          total += prevByDate[`${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`] ?? 0;
        }
        return total;
      }
      // monthly
      const [y, m] = dateKey.split('-').map(Number);
      const daysInMonth = new Date(y - 1, m, 0).getDate();
      let total = 0;
      for (let day = 1; day <= daysInMonth; day++) {
        total += prevByDate[`${y - 1}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`] ?? 0;
      }
      return total;
    };

    // Agrupar por período
    const grouped = new Map<string, { people: number; detectionCount: number }>();

    detections.forEach((detection) => {
      const date = new Date(detection.created_at);
      let key: string;

      switch (viewMode) {
        case 'daily':
          // Agrupar por dia (formato YYYY-MM-DD)
          key = date.toISOString().split('T')[0];
          break;
        case 'weekly':
          // Agrupar por semana (ISO Week) - começando na segunda-feira
          const startOfWeek = new Date(date);
          const day = startOfWeek.getDay();
          const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Ajustar para segunda-feira
          startOfWeek.setDate(diff);
          startOfWeek.setHours(0, 0, 0, 0);
          key = startOfWeek.toISOString().split('T')[0];
          break;
        case 'monthly':
          // Agrupar por mês (formato YYYY-MM)
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          key = `${year}-${month}-01`; // Primeiro dia do mês para facilitar ordenação
          break;
        default:
          key = date.toISOString().split('T')[0];
      }

      const current = grouped.get(key) || { people: 0, detectionCount: 0 };
      grouped.set(key, {
        people: current.people + detection.num_persons,
        detectionCount: current.detectionCount + 1,
      });
    });

    // Converter para array e ordenar cronologicamente
    const sortedData = Array.from(grouped.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, data]) => {
        let label: string;
        const dateObj = new Date(date + 'T00:00:00'); // Forçar timezone local

        switch (viewMode) {
          case 'daily':
            label = dateObj.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
            break;
          case 'weekly':
            const endOfWeek = new Date(dateObj);
            endOfWeek.setDate(endOfWeek.getDate() + 6);
            label = `${dateObj.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}`;
            break;
          case 'monthly':
            label = dateObj.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
            break;
          default:
            label = date;
        }

        return {
          label,
          people: data.people,
          date,
          detections: data.detectionCount,
        };
      });

    // Calcular média geral do período (linha de tendência horizontal)
    const totalPeople = sortedData.reduce((sum, d) => sum + d.people, 0);
    const avgPeople = totalPeople / sortedData.length;

    const withTrend = sortedData.map((item) => ({
      ...item,
      trend: Math.round(avgPeople),
      prev_year: hasPrevYearData ? getPrevYearValue(item.date) : undefined,
    }));

    return withTrend;
  }, [detections, viewMode, prevYearData]);

  // Calcular estatísticas
  const statistics = useMemo(() => {
    if (chartData.length === 0) {
      return { total: 0, average: 0, growth: 0, trend: 'neutral' as const, prevYearTotal: 0, diffPct: null as number | null };
    }

    const total = chartData.reduce((sum, d) => sum + d.people, 0);
    const average = Math.round(total / chartData.length);

    const halfPoint = Math.floor(chartData.length / 2);
    const firstHalf = chartData.slice(0, halfPoint);
    const secondHalf = chartData.slice(halfPoint);
    const firstAvg = firstHalf.reduce((sum, d) => sum + d.people, 0) / firstHalf.length;
    const secondAvg = secondHalf.reduce((sum, d) => sum + d.people, 0) / secondHalf.length;
    const growth = firstAvg > 0 ? ((secondAvg - firstAvg) / firstAvg) * 100 : 0;
    const trend = growth > 5 ? 'up' : growth < -5 ? 'down' : 'neutral';

    const prevYearTotal = chartData.reduce((sum, d) => sum + ((d as any).prev_year ?? 0), 0);
    const diffPct = prevYearTotal > 0
      ? Math.round(((total - prevYearTotal) / prevYearTotal) * 100)
      : null;

    return { total, average, growth: Math.round(growth), trend, prevYearTotal, diffPct };
  }, [chartData]);

  const hasPrevYear = React.useMemo(
    () => chartData.some(d => ((d as any).prev_year ?? 0) > 0),
    [chartData]
  );

  const viewModeLabels = {
    daily: 'Diário',
    weekly: 'Semanal',
    monthly: 'Mensal',
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 ${className}`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Calendar className="w-6 h-6 text-purple-600" />
            Comparativo Temporal
            <button
              onClick={() => setShowInfo((v) => !v)}
              title="Como funciona este gráfico"
              className="ml-1 text-gray-400 hover:text-purple-500 dark:hover:text-purple-400 transition-colors focus:outline-none"
            >
              {showInfo ? (
                <X className="w-4 h-4" />
              ) : (
                <Info className="w-4 h-4" />
              )}
            </button>
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Evolução do fluxo e tendências
          </p>
        </div>

        {/* Tabs de Visualização */}
        <div className="flex gap-2 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg">
          {(['daily', 'weekly', 'monthly'] as ViewMode[]).map((mode) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                viewMode === mode
                  ? 'bg-white dark:bg-gray-600 text-purple-600 dark:text-purple-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              {viewModeLabels[mode]}
            </button>
          ))}
        </div>
      </div>

      {/* Painel de Informações */}
      {showInfo && (
        <div className="mb-6 p-4 rounded-xl border border-purple-200 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/20 text-sm text-gray-700 dark:text-gray-300 space-y-2">
          <p className="font-semibold text-purple-700 dark:text-purple-300 flex items-center gap-2">
            <Info className="w-4 h-4" />
            Como funciona este gráfico
          </p>
          <ul className="space-y-1.5 list-none pl-1">
            <li>📅 <strong>Diário:</strong> cada ponto representa um dia do período selecionado. Permite ver a variação de fluxo dia a dia.</li>
            <li>📆 <strong>Semanal:</strong> os dias são agrupados por semana (segunda a domingo). Ideal para comparar semanas consecutivas.</li>
            <li>🗓️ <strong>Mensal:</strong> os dias são agrupados por mês. Útil para analisar tendências de longo prazo.</li>
            <li>📈 <strong>Linha roxa (Total de Pessoas):</strong> quantidade real de pessoas detectadas no período agrupado.</li>
            <li>📉 <strong>Linha azul tracejada (Média por Período):</strong> média geral do período selecionado — serve como linha de referência.</li>
            <li>🔼🔽 <strong>Tendência:</strong> compara a média da 1ª metade do período com a 2ª metade. Se o fluxo cresceu mais de 5%, mostra tendência positiva (verde); se caiu mais de 5%, negativa (vermelho).</li>
          </ul>
          <p className="text-xs text-gray-500 dark:text-gray-400 pt-1">
            💡 Use os filtros de câmera e período (hoje / semana / mês / personalizado) para refinar a análise.
          </p>
        </div>
      )}

      {/* Estatísticas */}
      <div className={`grid grid-cols-1 gap-4 mb-6 ${hasPrevYear ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-3'}`}>
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-xl p-4 border border-purple-200 dark:border-purple-700">
          <div className="text-sm text-purple-600 dark:text-purple-400 font-medium mb-1">
            Total de Pessoas
          </div>
          <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">
            {statistics.total.toLocaleString('pt-BR')}
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl p-4 border border-blue-200 dark:border-blue-700">
          <div className="text-sm text-blue-600 dark:text-blue-400 font-medium mb-1">
            Média por Período
          </div>
          <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
            {statistics.average.toLocaleString('pt-BR')}
          </div>
        </div>

        <div
          className={`bg-gradient-to-br rounded-xl p-4 border ${
            statistics.trend === 'up'
              ? 'from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200 dark:border-green-700'
              : statistics.trend === 'down'
              ? 'from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 border-red-200 dark:border-red-700'
              : 'from-gray-50 to-gray-100 dark:from-gray-700/20 dark:to-gray-600/20 border-gray-200 dark:border-gray-600'
          }`}
        >
          <div
            className={`text-sm font-medium mb-1 ${
              statistics.trend === 'up'
                ? 'text-green-600 dark:text-green-400'
                : statistics.trend === 'down'
                ? 'text-red-600 dark:text-red-400'
                : 'text-gray-600 dark:text-gray-400'
            }`}
          >
            Tendência
          </div>
          <div
            className={`text-2xl font-bold flex items-center gap-2 ${
              statistics.trend === 'up'
                ? 'text-green-900 dark:text-green-100'
                : statistics.trend === 'down'
                ? 'text-red-900 dark:text-red-100'
                : 'text-gray-900 dark:text-gray-100'
            }`}
          >
            {statistics.trend === 'up' ? (
              <TrendingUp className="w-5 h-5" />
            ) : statistics.trend === 'down' ? (
              <TrendingDown className="w-5 h-5" />
            ) : (
              <ArrowRight className="w-5 h-5" />
            )}
            {Math.abs(statistics.growth)}%
          </div>
        </div>

        {hasPrevYear && statistics.diffPct !== null && (
          <div className={`bg-gradient-to-br rounded-xl p-4 border ${
            statistics.diffPct >= 0
              ? 'from-teal-50 to-teal-100 dark:from-teal-900/20 dark:to-teal-800/20 border-teal-200 dark:border-teal-700'
              : 'from-rose-50 to-rose-100 dark:from-rose-900/20 dark:to-rose-800/20 border-rose-200 dark:border-rose-700'
          }`}>
            <div className={`text-sm font-medium mb-1 flex items-center gap-1 ${
              statistics.diffPct >= 0 ? 'text-teal-600 dark:text-teal-400' : 'text-rose-600 dark:text-rose-400'
            }`}>
              {statistics.diffPct > 0
                ? <TrendingUp className="w-4 h-4" />
                : statistics.diffPct < 0
                ? <TrendingDown className="w-4 h-4" />
                : <Minus className="w-4 h-4" />}
              vs Ano Anterior
            </div>
            <div className={`text-2xl font-bold ${
              statistics.diffPct >= 0 ? 'text-teal-900 dark:text-teal-100' : 'text-rose-900 dark:text-rose-100'
            }`}>
              {statistics.diffPct >= 0 ? '+' : ''}{statistics.diffPct}%
            </div>
            <div className={`text-xs mt-1 ${
              statistics.diffPct >= 0 ? 'text-teal-600 dark:text-teal-400' : 'text-rose-500 dark:text-rose-400'
            }`}>
              Ano anterior: {statistics.prevYearTotal.toLocaleString('pt-BR')}
            </div>
          </div>
        )}
      </div>

      {/* Gráfico */}
      {chartData.length === 0 ? (
        <div className="h-80 flex items-center justify-center text-gray-500 dark:text-gray-400">
          <div className="text-center">
            <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Nenhum dado disponível para o período selecionado</p>
          </div>
        </div>
      ) : (
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="colorPeople" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorTrend" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorPrevYear" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#14b8a6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 12, fill: '#6b7280' }}
                tickMargin={10}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '12px',
                }}
                formatter={(value: any, name: string) => {
                  const formattedValue = typeof value === 'number' ? value.toLocaleString('pt-BR') : value;
                  const label = name === 'people' ? 'Total de Pessoas'
                    : name === 'trend' ? 'Média por Período'
                    : name === 'prev_year' ? 'Ano Anterior'
                    : name;
                  return [formattedValue, label];
                }}
                labelFormatter={(label) => `Período: ${label}`}
              />
              <Legend
                wrapperStyle={{ paddingTop: '20px' }}
                formatter={(value) =>
                  value === 'people' ? 'Total de Pessoas'
                  : value === 'trend' ? 'Média por Período'
                  : value === 'prev_year' ? 'Ano Anterior'
                  : value
                }
              />
              {hasPrevYear && (
                <Area
                  type="monotone"
                  dataKey="prev_year"
                  stroke="#14b8a6"
                  strokeWidth={2}
                  strokeDasharray="4 4"
                  fill="url(#colorPrevYear)"
                  name="Ano Anterior"
                />
              )}
              <Area
                type="monotone"
                dataKey="people"
                stroke="#8b5cf6"
                strokeWidth={3}
                fill="url(#colorPeople)"
                name="Total de Pessoas"
              />
              <Area
                type="monotone"
                dataKey="trend"
                stroke="#3b82f6"
                strokeWidth={2}
                strokeDasharray="5 5"
                fill="url(#colorTrend)"
                name="Média por Período"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Insights */}
      {statistics.trend !== 'neutral' && chartData.length > 0 && (
        <div
          className={`mt-4 p-4 rounded-lg border ${
            statistics.trend === 'up'
              ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
              : 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800'
          }`}
        >
          <p
            className={`text-sm font-medium ${
              statistics.trend === 'up'
                ? 'text-green-700 dark:text-green-300'
                : 'text-red-700 dark:text-red-300'
            }`}
          >
            💡 Insight:{' '}
            {statistics.trend === 'up'
              ? `O fluxo aumentou ${Math.abs(statistics.growth)}% em relação ao período anterior. Continue monitorando essa tendência positiva.`
              : `O fluxo diminuiu ${Math.abs(statistics.growth)}% em relação ao período anterior. Considere analisar os fatores que podem ter contribuído para essa queda.`}
          </p>
        </div>
      )}
    </div>
  );
};
